import { Environment } from "../utils/Environment";
import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { postWithUserTableParams } from "../services/fetchWrapper";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

const apiUrl = Environment.getApiUrl();

interface ProcessDistanceRequest {
    job_id: string;
    compare_table_name: string;
    score_column_name: string;
    main_table_name: string;
    layer_name?: string;
    buffer_list: number[];
    score_list: number[];
    and_clause_filter?: string;
    is_srid_feet_clause?: string;
    client_id: string;
}

const RAILWAY_SCORE_COLUMNS = ["cof_rr_bo", "cof_rr_mis", "env_rr_lof", "cof_rr_fr"];

const EXCLUDED_SCORE_COLUMNS = [
    ...RAILWAY_SCORE_COLUMNS,
    "cof_wetlnd"
];

// Map expected score_column_name to display name
const EXPECTED_FLOWS: Record<string, string> = {
    cof_flowln: "flowline",
    cof_strctr: "structure",
    developed: "developed",
    cof_water: "water"
};

const ProcessCofScores: React.FC = () => {
    const [distanceScoreJsons, setDistanceScoreJsons] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [scoringComplete, setScoringComplete] = useState(false);

    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [pollingJobIds, setPollingJobIds] = useState<string[]>([]);
    const [batchPollingActive, setBatchPollingActive] = useState(false);
    const [jobStatusMap, setJobStatusMap] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!batchPollingActive || pollingJobIds.length === 0) return;

        // Find incomplete jobs based on their individual status
        let incompleteIds = pollingJobIds.filter(
            id => !["completed", "success", "failed", "unknown", "error"].includes(jobStatusMap[id] ?? "")
        );

        // Sort IDs numerically, smallest to largest
        incompleteIds = incompleteIds.sort((a, b) => Number(a) - Number(b));

        if (incompleteIds.length === 0) {
            setUploadStatus("complete");
            setStatusMessage("All jobs complete.");
            setPollingJobIds([]);
            setBatchPollingActive(false);
            setScoringComplete(true);
            localStorage.setItem("cofScoresDone", "true");
            return;
        }

        // Poll one job per interval
        const idToPoll = incompleteIds[0];
        const timer = setTimeout(async () => {
            try {
                const response = await fetch(`${apiUrl}/upload-status/${idToPoll}`);
                if (response.ok) {
                    const data = await response.json();
                    setJobStatusMap(prev => ({
                        ...prev,
                        [idToPoll]: data.upload_status
                    }));
                    setUploadStatus(data.upload_status);
                    setStatusMessage(data.message);
                } else {
                    setStatusMessage("Failed to get upload status.");
                }
            } catch (err) {
                setStatusMessage("Error polling job status.");
            }
        }, 10000); // poll every 10 seconds

        return () => clearTimeout(timer);
    }, [batchPollingActive, pollingJobIds, jobStatusMap, apiUrl]);


    // Fetch client data on mount
    useEffect(() => {
        const fetchClientData = async () => {
            // Get values from localStorage at function run time
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";

            setLoading(true);
            setError(null);
            try {
                const response = await postWithUserTableParams(`${apiUrl}/get-client-data-for-distance-scoring`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch client data");
                }
                const data = await response.json();
                if (data.status !== 'success' || !Array.isArray(data.jsons)) {
                    throw new Error("Invalid response format from backend");
                }
                // filter out railway and wetland JSONs
                const filtered = data.jsons.filter(
                    (j: any) => !EXCLUDED_SCORE_COLUMNS.includes((j.score_column_name || "").toLowerCase())
                );
                setDistanceScoreJsons(filtered);
            } catch (err: any) {
                setError(err.message || "Failed to fetch client data");
            } finally {
                setLoading(false);
            }
        };
        fetchClientData();
    }, [apiUrl]);

    // Map expected flows to found/not found based on score_column_name
    const flowStatus = Object.entries(EXPECTED_FLOWS).map(([scoreCol, displayName]) => {
        const found = distanceScoreJsons.some(j => (j.score_column_name || "").toLowerCase() === scoreCol.toLowerCase());
        const tableName = found
            ? distanceScoreJsons.find(j => (j.score_column_name || "").toLowerCase() === scoreCol.toLowerCase())?.compare_table_name || "N/A"
            : null;
        return { scoreCol, displayName, found, tableName };
    });


    const handleProcessDistanceScores = async () => {
        setLoading(true);
        setError(null);
        setScoringComplete(false);
        setUploadStatus(null);
        setStatusMessage(null);
        setPollingJobIds([]);
        setBatchPollingActive(false);

        const batchRequests: ProcessDistanceRequest[] = distanceScoreJsons.map((raw: any) => ({
            job_id: raw.job_id,
            compare_table_name: raw.compare_table_name,
            score_column_name: raw.score_column_name,
            main_table_name: raw.main_table_name,
            layer_name: raw.layer_name,
            buffer_list: raw.buffer_list,
            score_list: raw.score_list,
            and_clause_filter: raw.and_clause_filter,
            is_srid_feet_clause: raw.is_srid_feet_clause,
            client_id: raw.client_id
        }));

        try {
            const response = await fetch(`${apiUrl}/process-distance-score`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(batchRequests),
            });
            if (!response.ok) {
                setUploadStatus("error");
                setStatusMessage("Failed to process distance scores");
                throw new Error("Failed to process distance scores");
            }
            const data = await response.json();
            setStatusMessage(data.message || "Batch started...");
            setUploadStatus("processing");
            if (Array.isArray(data.ids) && data.ids.length > 0) {
                setPollingJobIds(data.ids);
                setBatchPollingActive(true); // Start polling only if we have job IDs
            } else {
                setStatusMessage("Waiting for jobs to start...");
                setUploadStatus("processing");
                // Optionally, you could show a spinner or message until jobs are available
            }
        } catch (err: any) {
            setError(err.message || "Failed to process distance scores");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textTransform: "uppercase" }}>
                Proximity Distance Scoring
            </Typography>
            <svg width="292" height="6" viewBox="0 0 292 6" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16 }}>
                <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
            </svg>
            <Typography
                variant="body2"
                sx={{ mb: 3, fontSize: 16, maxWidth: 450, mx: "auto", textAlign: "center" }}
            >
                Blue checkmarks indicate that your tables were successfully retrieved. An "X" indicates that the data is not present. This is okay, we just need one of these values to process proximity scoring. If you are not missing any datasets, continue.
            </Typography>
            <TableContainer
                component={Paper}
                sx={{
                    mt: 2,
                    mb: 3,
                    maxWidth: 620,
                    width: "100%",
                    mx: "auto",
                }}
            >
                <Table size="small" aria-label="distance scoring data status table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Dataset Name</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Table</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, width: 100 }}>Uploaded Data</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {flowStatus.map(({ scoreCol, displayName, found, tableName }, index) => (
                            <TableRow
                                key={scoreCol}
                                sx={{
                                    backgroundColor: index % 2 === 0 ? "background.paper" : "grey.100",
                                }}
                            >
                                <TableCell>{displayName}</TableCell>
                                <TableCell>
                                    {found ? `Data found (${tableName})` : "No data found"}
                                </TableCell>
                                <TableCell align="right">
                                    <Box
                                        sx={{
                                            ml: "auto",
                                            width: 34,
                                            height: 34,
                                            borderRadius: 1,
                                            border: found ? "1px solid" : "none",
                                            borderColor: found ? "success.main" : "transparent",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: found ? "#0373B5" : "transparent",
                                        }}
                                    >
                                        {found ? (
                                            <CheckCircleIcon fontSize="small" sx={{ color: "#FFFFFF" }}/>
                                        ) : (
                                            <HighlightOffIcon fontSize="small" sx={{ color: "#828282" }} />
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button
                variant="contained"
                color="primary"
                disabled={loading || distanceScoreJsons.length === 0}
                onClick={handleProcessDistanceScores}
                sx={{ backgroundColor: "#0373B5" }}
            >
                {loading ? "Processing..." : "Process distance scores"}
            </Button>
            {scoringComplete && (
                <Typography color="success.main" sx={{ mt: 2 }}>
                    Scoring is complete!
                </Typography>
            )}
            {uploadStatus && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        maxWidth: 500,
                        width: "100%",
                        mx: "auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6">Processing Status</Typography>
                    <Typography variant="body2">
                        Status: {uploadStatus}
                    </Typography>
                    <Typography variant="body2">
                        Message: {statusMessage}
                    </Typography>
                </Box>
            )}
            {batchPollingActive && (
                <Box sx={{
                    mt: 2,
                    p: 2,
                    maxWidth: 500,
                    width: "100%",
                    mx: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
                >
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    <Typography variant="body2">Processing, please wait...</Typography>
                </Box>
            )}
            {error && <div style={{ color: "red" }}>{error}</div>}
        </Box>
    );
};

export default ProcessCofScores;